/**
 * Recherche d'adresse au fil de la frappe, adossée à **Photon**
 * (<https://photon.komoot.io>) — moteur libre bâti sur les données
 * OpenStreetMap, hébergé gratuitement par Komoot, sans clé d'API.
 *
 * ⚠️ **Ce fichier est un portage de `wargame-app`, à l'identique.** Les deux
 * dépôts doivent chercher les mêmes adresses et les classer pareil : une
 * divergence ici se verrait comme deux applications qui ne trouvent pas la
 * même chose sur la même frappe. Une évolution se fait là-bas d'abord, puis se
 * recopie ici — comme la palette.
 *
 * Module **pur** : aucun import React, il est couvert par son test à côté.
 * C'est le seul endroit à modifier pour faire évoluer la recherche —
 * l'interface n'en connaît que la sortie.
 *
 * ## Pourquoi pas Nominatim
 *
 * L'implémentation précédente interrogeait Nominatim. Sa politique d'usage
 * l'interdit explicitement pour cet usage : *« Auto-complete search: this is
 * not yet supported by Nominatim and you must not implement such a service on
 * top of our API »*, avec un plafond d'une requête par seconde — là où l'app
 * en envoyait **trois par frappe déboucée**. Photon existe précisément pour ce
 * cas : il indexe les mêmes données, tolère les fautes de frappe et répond sur
 * un préfixe.
 *
 * ## Pourquoi les doublons ont disparu
 *
 * Deux causes, toutes deux traitées ici :
 *
 * 1. **Trois requêtes concurrentes** (France, reste de l'UE, monde) dont la
 *    troisième renvoyait forcément ce que la première avait déjà trouvé. Une
 *    seule requête suffit désormais, la pertinence géographique passant par
 *    `lat`/`lon` (cf. `bias`) plutôt que par des filtres pays cumulés.
 * 2. **Un dédoublonnage sur `place_id`**, l'identifiant interne de Nominatim :
 *    « Lyon » existe en base comme nœud *et* comme relation administrative,
 *    donc deux identifiants pour un seul lieu aux yeux de l'utilisateur. On
 *    dédoublonne maintenant sur l'identifiant **OSM** (stable) *et* sur le
 *    libellé affiché, normalisé — c'est ce dernier qui fait le travail.
 */

export type GeocodingResult = {
  /** Identifiant OSM stable (`R120965`), ou le libellé en dernier recours. */
  id: string;
  latitude: number;
  longitude: number;
  /** Ce qui s'affiche dans la liste, et ce qui sera mémorisé si on le choisit. */
  label: string;
};

/** Sous-ensemble de la réponse Photon qui nous intéresse. */
type PhotonProperties = {
  osm_id?: number;
  osm_type?: string;
  name?: string;
  housenumber?: string;
  street?: string;
  postcode?: string;
  city?: string;
  town?: string;
  village?: string;
  state?: string;
  country?: string;
};

type PhotonFeature = {
  geometry?: { coordinates?: number[] };
  properties?: PhotonProperties;
};

export type PhotonResponse = { features?: PhotonFeature[] };

const PHOTON_URL = "https://photon.komoot.io/api/";

/** Langues indexées par Photon ; toute autre valeur retombe sur l'anglais. */
const SUPPORTED_LANGUAGES = new Set(["de", "en", "fr", "it"]);

/**
 * On demande plus large qu'on n'affiche : le dédoublonnage par libellé retire
 * souvent deux ou trois entrées, et une liste tronquée à 6 en amont
 * ressortirait à 3.
 */
const FETCH_LIMIT = 15;
const MAX_SUGGESTIONS = 6;

export type SearchOptions = {
  /**
   * Position de l'utilisateur. Photon s'en sert pour classer : « lyon 3 »
   * remonte l'arrondissement plutôt qu'une rue homonyme à l'autre bout du
   * monde. C'est le principal gain de précision, et il ne coûte rien.
   */
  bias?: { latitude: number; longitude: number } | null;
  signal?: AbortSignal;
};

export async function searchAddresses(
  text: string,
  locale: string,
  options: SearchOptions = {},
): Promise<GeocodingResult[]> {
  const params = new URLSearchParams({
    q: text,
    limit: String(FETCH_LIMIT),
    lang: SUPPORTED_LANGUAGES.has(locale) ? locale : "en",
  });

  if (options.bias) {
    params.set("lat", String(options.bias.latitude));
    params.set("lon", String(options.bias.longitude));
  }

  const res = await fetch(`${PHOTON_URL}?${params}`, {
    headers: { Accept: "application/json" },
    signal: options.signal,
  });

  if (!res.ok) throw new Error(`Photon a répondu ${res.status}`);

  return toResults((await res.json()) as PhotonResponse, text);
}

/**
 * Transforme une réponse Photon en suggestions prêtes à afficher :
 * conversion, dédoublonnage, classement, troncature.
 *
 * Séparée de `searchAddresses` pour être testable sans réseau — c'est ici que
 * vit toute la logique, l'appel HTTP n'étant qu'une couche de transport.
 */
export function toResults(
  body: PhotonResponse,
  query: string,
): GeocodingResult[] {
  const features = Array.isArray(body?.features) ? body.features : [];
  const results = features.flatMap(featureToResult);
  return rankResults(deduplicate(results), query).slice(0, MAX_SUGGESTIONS);
}

function featureToResult(feature: PhotonFeature): GeocodingResult[] {
  // Photon publie du GeoJSON : les coordonnées sont [longitude, latitude],
  // dans cet ordre. Les inverser placerait Lyon au large de la Somalie.
  const longitude = feature.geometry?.coordinates?.[0];
  const latitude = feature.geometry?.coordinates?.[1];
  if (typeof latitude !== "number" || typeof longitude !== "number") return [];

  const properties = feature.properties ?? {};
  const label = buildLabel(properties);
  // Un résultat sans libellé n'est pas choisissable : mieux vaut ne pas
  // l'afficher qu'afficher une ligne vide.
  if (!label) return [];

  const { osm_type, osm_id } = properties;
  return [
    {
      id: osm_type && osm_id !== undefined ? `${osm_type}${osm_id}` : label,
      latitude,
      longitude,
      label,
    },
  ];
}

/**
 * Libellé lisible, du plus précis au plus général.
 *
 * Chaque échelon n'est ajouté que s'il apporte une information nouvelle :
 * répéter « Lyon, Lyon, France » n'aide personne à choisir entre deux
 * résultats, et c'est exactement ce que produit une concaténation naïve des
 * champs renvoyés par le serveur.
 */
export function buildLabel(properties: PhotonProperties): string {
  const street = properties.street
    ? properties.housenumber
      ? `${properties.housenumber} ${properties.street}`
      : properties.street
    : null;

  const head = street ?? properties.name ?? null;
  const locality =
    properties.city ?? properties.town ?? properties.village ?? null;
  const region = properties.state ?? null;

  const parts: string[] = [];
  if (head) parts.push(head);
  if (locality && locality !== head) parts.push(locality);

  // Le code postal n'a de sens que sur une adresse précise. Celui d'une ville
  // entière est arbitraire — Lyon en compte neuf — et n'aide pas à distinguer
  // deux résultats homonymes.
  if (street && properties.postcode) parts.push(properties.postcode);

  if (region && region !== head && region !== locality) parts.push(region);
  if (properties.country && properties.country !== head) {
    parts.push(properties.country);
  }

  return parts.join(", ");
}

/**
 * Diacritiques combinants, que `NFD` vient de détacher de leurs lettres.
 *
 * Construite depuis une chaîne plutôt qu'écrite en littéral : les caractères
 * de cette plage sont **invisibles** dans un éditeur, et un littéral
 * ressemble à s'y méprendre à une coquille qu'un relecteur « corrigerait ».
 */
const COMBINING_MARKS = new RegExp("[\\u0300-\\u036f]", "g");

/**
 * Minuscules, accents retirés, espaces normalisés — pour comparer des
 * libellés, jamais pour en afficher un. « Saint-Étienne » et « Saint-Etienne »
 * doivent se reconnaître.
 */
function normalize(label: string): string {
  return label
    .normalize("NFD")
    .replace(COMBINING_MARKS, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Retire les répétitions, par identifiant OSM **et** par libellé affiché.
 *
 * Le second critère est celui qui compte : deux objets OSM distincts — le
 * nœud « Lyon » et la relation administrative « Lyon » — ont deux
 * identifiants mais produisent la même ligne à l'écran. Pour l'utilisateur,
 * c'est un doublon.
 */
export function deduplicate(results: GeocodingResult[]): GeocodingResult[] {
  const seenIds = new Set<string>();
  const seenLabels = new Set<string>();

  return results.filter((result) => {
    const label = normalize(result.label);
    if (seenIds.has(result.id) || seenLabels.has(label)) return false;
    seenIds.add(result.id);
    seenLabels.add(label);
    return true;
  });
}

/**
 * Fait remonter ce qui commence par ce qui a été tapé.
 *
 * Photon classe déjà par pertinence, mais sur « paris » il place volontiers
 * une « rue de Paris » quelque part avant Paris. Quelqu'un qui tape le nom
 * d'une ville cherche la ville.
 *
 * Le tri est **stable** : à égalité, l'ordre de Photon est conservé, ce qui
 * préserve son classement par importance et par proximité.
 *
 * ⚠️ **Seule divergence avec `wargame-app`** : `toSorted` au lieu de `sort`,
 * qu'`oxlint` refuse ici (`unicorn/no-array-sort`). Le tableau vient d'un
 * `map`, donc la mutation était sans effet — c'est le même résultat, et la
 * spécification garantit la même stabilité.
 */
export function rankResults(
  results: GeocodingResult[],
  query: string,
): GeocodingResult[] {
  const needle = normalize(query);
  if (!needle) return results;

  return results
    .map((result, index) => ({
      result,
      index,
      startsWithQuery: normalize(result.label).startsWith(needle),
    }))
    .toSorted((a, b) => {
      if (a.startsWithQuery !== b.startsWithQuery) {
        return a.startsWithQuery ? -1 : 1;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.result);
}
